/**
 * Altar Forge resources as display data (pure, DOM-free): labels, colours, ordering, bundle
 * formatting, wallet rows and "you are missing…" text. Every forge screen reads these so a
 * resource is always called and coloured the same way.
 */
import { EYE_FRAGMENTS_PER_EYE, type Profile, type ResourceBundle, type ScaleGrade, SCALE_GRADES } from '@keystone/progression';

export type ResourceKey = 'fangsteel' | `scale.${ScaleGrade}` | 'venom' | 'moltskin' | 'eyeFragments' | 'sigils' | 'blessed' | 'gold';

export interface ResourceMeta {
  label: string;
  /** Singular label (1 Sigil). */
  one: string;
  /** Short label for tight chips ("Jade"). */
  short: string;
  color: string;
  /** One-line "what it is for" (tooltips, the wallet panel). */
  use: string;
}

export const RESOURCE_ORDER: readonly ResourceKey[] = ['sigils', 'fangsteel', 'scale.ashen', 'scale.jade', 'scale.gilded', 'scale.eternal', 'venom', 'moltskin', 'eyeFragments', 'blessed', 'gold'];

export const SCALE_LABEL: Readonly<Record<ScaleGrade, string>> = { ashen: 'Ashen', jade: 'Jade', gilded: 'Gilded', eternal: 'Eternal' };

export const RESOURCE_META: Readonly<Record<ResourceKey, ResourceMeta>> = {
  fangsteel: { label: 'Fangsteel', one: 'Fangsteel', short: 'Fangsteel', color: '#8fb59a', use: 'The bulk of every craft and temper.' },
  'scale.ashen': { label: 'Ashen Scales', one: 'Ashen Scale', short: 'Ashen', color: '#b9bcc4', use: 'Crafts at item level 210; tempers up to 220.' },
  'scale.jade': { label: 'Jade Scales', one: 'Jade Scale', short: 'Jade', color: '#4fd39a', use: 'Crafts at item level 225; tempers up to 240.' },
  'scale.gilded': { label: 'Gilded Scales', one: 'Gilded Scale', short: 'Gilded', color: '#e8c060', use: 'Crafts at item level 240; tempers up to 255.' },
  'scale.eternal': { label: 'Eternal Scales', one: 'Eternal Scale', short: 'Eternal', color: '#c9b3ff', use: 'Crafts at item level 255; tempers up to 270.' },
  venom: { label: 'Viper Venom', one: 'Viper Venom', short: 'Venom', color: '#7dff6a', use: 'Luck: +1 per vial on a craft, +5% per vial on a temper.' },
  moltskin: { label: 'Moltskin', one: 'Moltskin', short: 'Moltskin', color: '#e6d6c0', use: 'Control: lock stats when crafting, refine stat quality.' },
  eyeFragments: { label: 'Eye Fragments', one: 'Eye Fragment', short: 'Eye', color: '#ff9a3c', use: `${EYE_FRAGMENTS_PER_EYE} fragments make a Serpent’s Eye: a Legendary roll.` },
  sigils: { label: 'Altar Sigils', one: 'Altar Sigil', short: 'Sigils', color: '#3fe0d0', use: 'Turns a craft into a tier set piece.' },
  blessed: { label: 'Blessed Charges', one: 'Blessed Charge', short: 'Blessed', color: '#ffe9a8', use: '+3 Luck on a craft or +15% on a temper.' },
  gold: { label: 'Gold', one: 'Gold', short: 'Gold', color: '#ffd76a', use: 'The fee on crafts, tempers and combines.' },
};

export const scaleKey = (g: ScaleGrade): ResourceKey => `scale.${g}`;

export interface ResourceAmount {
  key: ResourceKey;
  n: number;
}

/** A bundle as ordered (key, amount) pairs; zero amounts are dropped. */
export function bundleEntries(b: ResourceBundle | null | undefined): ResourceAmount[] {
  if (!b) return [];
  const out: ResourceAmount[] = [];
  for (const key of RESOURCE_ORDER) {
    const n = amountIn(b, key);
    if (n) out.push({ key, n });
  }
  return out;
}

/** Amount of one resource in a bundle. */
export function amountIn(b: ResourceBundle, key: ResourceKey): number {
  if (key.startsWith('scale.')) return b.scales?.[key.slice(6) as ScaleGrade] ?? 0;
  return (b[key as Exclude<ResourceKey, `scale.${ScaleGrade}`>] as number | undefined) ?? 0;
}

/** What the player holds of one resource. */
export function owned(p: Pick<Profile, 'wallet' | 'currencies'>, key: ResourceKey): number {
  if (key === 'gold') return p.currencies.gold;
  if (key.startsWith('scale.')) return p.wallet.scales[key.slice(6) as ScaleGrade] ?? 0;
  return (p.wallet[key as 'fangsteel' | 'venom' | 'moltskin' | 'eyeFragments' | 'sigils' | 'blessed'] as number) ?? 0;
}

const fmt = (n: number): string => String(Math.round(Math.abs(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** "3 Jade Scales" / "1 Altar Sigil". */
export function amountText(a: ResourceAmount): string {
  const m = RESOURCE_META[a.key];
  return `${fmt(a.n)} ${Math.abs(a.n) === 1 ? m.one : m.label}`;
}

/** "2 Jade Scales, 6 Fangsteel" (empty bundle → `empty`). */
export function bundleText(b: ResourceBundle | null | undefined, empty = 'nothing'): string {
  const e = bundleEntries(b);
  return e.length ? e.map(amountText).join(', ') : empty;
}

/** Short "after this roll" text: what is left of each resource the bundle spends. */
export function afterText(p: Pick<Profile, 'wallet' | 'currencies'>, cost: ResourceBundle): string {
  return bundleEntries(cost)
    .filter((a) => a.key !== 'gold')
    .map((a) => `${fmt(Math.max(0, owned(p, a.key) - a.n))} ${RESOURCE_META[a.key].short}`)
    .join(' · ');
}

/** "Need 4 more Jade Scales and 120 more gold." or null when nothing is missing. */
export function shortfallText(short: ResourceBundle | null | undefined): string | null {
  const e = bundleEntries(short);
  if (!e.length) return null;
  const parts = e.map((a) => `${fmt(a.n)} more ${Math.abs(a.n) === 1 ? RESOURCE_META[a.key].one : RESOURCE_META[a.key].label}`);
  const last = parts.pop()!;
  return `Need ${parts.length ? `${parts.join(', ')} and ${last}` : last}.`;
}

export interface WalletRow extends ResourceAmount {
  meta: ResourceMeta;
  /** Eye fragments: whole Eyes held (else undefined). */
  eyes?: number;
}

/** Every wallet resource (zeros included unless `hideZero`), in display order. Gold excluded unless asked. */
export function walletRows(p: Pick<Profile, 'wallet' | 'currencies'>, opts: { hideZero?: boolean; gold?: boolean } = {}): WalletRow[] {
  const out: WalletRow[] = [];
  for (const key of RESOURCE_ORDER) {
    if (key === 'gold' && !opts.gold) continue;
    const n = owned(p, key);
    if (opts.hideZero && !n) continue;
    const row: WalletRow = { key, n, meta: RESOURCE_META[key] };
    if (key === 'eyeFragments') row.eyes = Math.floor(n / EYE_FRAGMENTS_PER_EYE);
    out.push(row);
  }
  return out;
}

/** Scale grades the wallet holds, lowest first (for the craft recipe's grade picker). */
export function scaleHoldings(p: Pick<Profile, 'wallet'>): { grade: ScaleGrade; n: number }[] {
  return SCALE_GRADES.map((grade) => ({ grade, n: p.wallet.scales[grade] ?? 0 }));
}
